import { sha256 } from "js-sha256";
import bs58 from "bs58";

/**
 * Ensures all provided values are either undefined/null or valid Uint8Array instances.
 * Throws an error if any provided value is not a Uint8Array.
 *
 * @param {Object} fields - A map of field names to values to validate.
 * @throws {TypeError} If any value is not a Uint8Array (and not null/undefined).
 */
export function assertUint8Arrays(fields) {
  for (const [key, value] of Object.entries(fields)) {
    if (value == null) continue; // Allow null or undefined
    if (!(value instanceof Uint8Array)) {
      throw new TypeError(`Field "${key}" must be a Uint8Array, got ${typeof value}`);
    }
  }
}

/**
 * Hashes a Uint8Array by first base58-encoding it, then applying SHA-256.
 * Used for indexing keys like conversation IDs.
 * 
 * @param {Uint8Array} uint8ArrayInput
 * @returns {Promise<string>} hashed value
 */
export async function hashUint8Array(uint8ArrayInput) {
  if (!(uint8ArrayInput instanceof Uint8Array)) {
    throw new Error("Expected Uint8Array input");
  }
  const base58Encoded = bs58.encode(uint8ArrayInput);
  return sha256(base58Encoded);
}

export function openDB(name, version, upgradeCallback) {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(name, version);

    request.onerror = () => reject(request.error);

    request.onupgradeneeded = (e) => {
      const db = e.target.result;
      try {
        upgradeCallback?.(db);
      } catch (err) {
        reject(err);
      }
    };

    request.onsuccess = () => {
      resolve(request.result); // always resolves after upgrade completes
    };
  });
}
export function promisifyRequest(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export function getFromStore(store, key) {
  return new Promise((resolve, reject) => {
    const request = key !== undefined ? store.get(key) : store.getAll();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export function putToStore(store, value) {
  return new Promise((resolve, reject) => {
    const request = store.put(value);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Gets all records from an IndexedDB index that match the given key.
 * 
 * @param {IDBIndex} index - The index to query.
 * @param {string|number|IDBKeyRange} key - The key to match in the index.
 * @returns {Promise<Array>} - Resolves to an array of matched records.
 */
export function getAllFromIndex(index, key) {
  return new Promise((resolve, reject) => {
    const request = index.getAll(key);
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}